import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { usePaged, PageSize, Pager, ModuleTabs } from "@/components/Toolbar";
import { Plus, Trash2, Pencil, Eye, ArrowLeftRight, Search, RotateCw, FileSpreadsheet, Printer, Check } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { SortHeader, useOwnerSort } from "@/components/SortHeader";

const VEHICLE_TYPES = ["Open", "Container", "Trailer", "Tipper", "Bulker"];
const STATES = ["Karnataka", "Kerala", "Andhra Pradesh", "Tamil Nadu", "Maharashtra", "Goa", "Telangana"];
const dt = (s) => (s ? new Date(s).toLocaleDateString("en-GB") : "-");
const Field = ({ label, children, req }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}{req && <span className="text-rose-500"> *</span>}</label>
    {children}
  </div>
);
const IconBtn = ({ children, ...p }) => (
  <button className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-accent" {...p}>{children}</button>
);

export default function Onboarding() {
  const [tab, setTab] = useState("owner");
  return (
    <div>
      <h1 className="font-display text-2xl font-bold mb-6">Onboarding</h1>
      <ModuleTabs
        tabs={[
          { id: "owner", label: "Owner Master" },
          { id: "truck", label: "Truck Master" },
          { id: "master", label: "Master Data" },
          { id: "transfer", label: "Owner Transfer Log" },
          { id: "activity", label: "Activity Log" },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === "owner" && <OwnerMaster />}
      {tab === "truck" && <TruckMaster />}
      {tab === "master" && <MasterData />}
      {tab === "transfer" && <TransferLog />}
      {tab === "activity" && <ActivityLog />}
    </div>
  );
}

function Toolbar({ q, setQ, onRefresh, onExport, right, placeholder }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      <div className="flex items-center gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9 w-64" placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} data-testid="onb-search" />
        </div>
        <IconBtn onClick={onRefresh} data-testid="onb-refresh"><RotateCw className="h-4 w-4" /></IconBtn>
      </div>
      <div className="flex items-center gap-2">
        <IconBtn onClick={onExport} data-testid="onb-export"><FileSpreadsheet className="h-4 w-4" /></IconBtn>
        <IconBtn onClick={() => window.print()} data-testid="onb-print"><Printer className="h-4 w-4" /></IconBtn>
        {right}
      </div>
    </div>
  );
}

/* ---------------- Owner Master ---------------- */
function OwnerMaster() {
  const { hubs, selectedHub } = useApp();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const emptyBank = { ifsc: "", bank_name: "", branch: "", account_number: "", holder_name: "" };
  const empty = { name: "", phone: "", email: "", pan: "", state: "", district: "", banks: [{ ...emptyBank }], hub_id: "" };
  const [f, setF] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => setRows((await api.get("/owners", { params: { hub: selectedHub } })).data), [selectedHub]);
  useEffect(() => { load(); api.get("/vehicles", { params: { hub: selectedHub } }).then((r) => setVehicles(r.data)); }, [load, selectedHub]);

  const filtered = rows.filter((r) => !q || (r.name + r.email + r.district + r.state).toLowerCase().includes(q.toLowerCase()));
  const { sorted: osRows, dir: osDir, toggle: osToggle } = useOwnerSort(filtered, "name");
  const { page, setPage, pageSize, setPageSize, totalPages, pageRows, start } = usePaged(osRows);

  const openCreate = () => { setEditing(null); setF({ ...empty, hub_id: selectedHub !== "all" ? selectedHub : hubs[0]?.id || "" }); setOpen(true); };
  const openEdit = (r) => { setEditing(r.id); setF({ ...empty, ...r, banks: r.banks?.length ? r.banks : [{ ...emptyBank }] }); setOpen(true); };
  const save = async () => {
    if (!f.name || !f.phone) return toast.error("Owner Name and Mobile Number are required");
    if (editing) { await api.put(`/owners/${editing}`, f); toast.success("Owner updated"); }
    else { await api.post("/owners", f); toast.success("Truck owner added"); }
    setOpen(false); load();
  };
  const del = async (id) => { await api.delete(`/owners/${id}`); toast.success("Deleted"); load(); };
  const setBank = (i, k, v) => setF((p) => ({ ...p, banks: p.banks.map((b, j) => (j === i ? { ...b, [k]: v } : b)) }));
  const lookupIfsc = async (i, code) => {
    const c = (code || "").trim().toUpperCase();
    if (c.length < 11) return;
    try {
      const { data } = await api.get(`/ifsc/${c}`);
      setF((p) => ({ ...p, banks: p.banks.map((b, j) => (j === i ? { ...b, bank_name: data.bank, branch: data.branch } : b)) }));
      toast.success(`${data.bank} · ${data.branch}`);
    } catch { toast.error("Invalid IFSC code"); }
  };
  const exportCsv = () => {
    const lines = filtered.map((r) => [r.name, r.district, r.state, r.email, r.phone].join(","));
    const blob = new Blob([["Owner Name,District,State,Email,Phone", ...lines].join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "owners.csv"; a.click();
  };

  return (
    <div>
      <Toolbar q={q} setQ={setQ} onRefresh={load} onExport={exportCsv} placeholder="Search owner name"
        right={<><PageSize value={pageSize} onChange={setPageSize} /><Button onClick={openCreate} data-testid="add-owner-button"><Plus className="h-4 w-4 mr-1" />Add Truck Owner</Button></>} />
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="owner-master-table">
          <thead className="bg-muted/60 text-muted-foreground text-xs uppercase">
            <tr><th className="py-3 px-4 text-left">Sl No</th><SortHeader label="Owner Name" dir={osDir} onClick={osToggle} /><th className="py-3 px-4 text-left">District</th><th className="py-3 px-4 text-left">State</th><th className="py-3 px-4 text-left">Email ID</th><th className="py-3 px-4 text-left">Mobile</th><th className="py-3 px-4 text-center">Action</th></tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => (
              <tr key={r.id} className="border-t border-border hover:bg-accent/40" data-testid={`owner-master-row-${r.id}`}>
                <td className="py-3 px-4 text-muted-foreground">{start + i + 1}</td>
                <td className="py-3 px-4 font-medium">{r.name}</td>
                <td className="py-3 px-4">{r.district || "-"}</td>
                <td className="py-3 px-4">{r.state || "-"}</td>
                <td className="py-3 px-4 text-muted-foreground">{r.email || "-"}</td>
                <td className="py-3 px-4 font-mono">{r.phone || "-"}</td>
                <td className="py-3 px-4">
                  <div className="flex items-center justify-center gap-3">
                    <button onClick={() => setView(r)} className="text-muted-foreground hover:text-foreground" data-testid={`view-owner-${r.id}`}><Eye className="h-4 w-4" /></button>
                    <button onClick={() => openEdit(r)} className="text-primary hover:opacity-70" data-testid={`edit-owner-${r.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(r.id)} className="text-rose-500 hover:opacity-70" data-testid={`delete-owner-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {pageRows.length === 0 && <tr><td colSpan={7} className="py-16 text-center text-muted-foreground">No owners</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} totalPages={totalPages} setPage={setPage} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="owner-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Truck Owner" : "Create Truck Owner"}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Vehicle Owner Information</p>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Owner Name" req><Input data-testid="owner-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
            <Field label="Mobile Number" req><Input data-testid="owner-phone-input" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
            <Field label="Email ID"><Input data-testid="owner-email-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
            <Field label="PAN Card No"><Input data-testid="owner-pan-input" value={f.pan} onChange={(e) => setF({ ...f, pan: e.target.value.toUpperCase() })} /></Field>
            <Field label="State">
              <Select value={f.state} onValueChange={(v) => setF({ ...f, state: v })}>
                <SelectTrigger data-testid="owner-state-select"><SelectValue placeholder="Select State" /></SelectTrigger>
                <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="District"><Input data-testid="owner-district-input" value={f.district} onChange={(e) => setF({ ...f, district: e.target.value })} /></Field>
          </div>

          <div className="flex items-center justify-between mt-2">
            <div><p className="font-semibold text-sm">Owner Bank Details</p><p className="text-xs text-muted-foreground">Enter bank details</p></div>
            <button className="text-sm text-primary font-medium" onClick={() => setF((p) => ({ ...p, banks: [...p.banks, { ...emptyBank }] }))} data-testid="add-bank-button">+ Add Bank</button>
          </div>
          {f.banks.map((b, i) => (
            <div key={i} className="bg-muted/40 rounded-lg p-4 space-y-3" data-testid={`bank-block-${i}`}>
              <div className="flex items-center justify-between"><span className="text-xs font-semibold text-muted-foreground">Bank {i + 1}</span>
                {f.banks.length > 1 && <button className="text-rose-500 text-xs" onClick={() => setF((p) => ({ ...p, banks: p.banks.filter((_, j) => j !== i) }))}>Remove</button>}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Input placeholder="IFSC Code" value={b.ifsc} onChange={(e) => setBank(i, "ifsc", e.target.value.toUpperCase())} onBlur={(e) => lookupIfsc(i, e.target.value)} data-testid={`bank-ifsc-${i}`} />
                <Input placeholder="Bank Name (auto)" value={b.bank_name} onChange={(e) => setBank(i, "bank_name", e.target.value)} data-testid={`bank-name-${i}`} />
                <Input placeholder="Bank Branch (auto)" value={b.branch} onChange={(e) => setBank(i, "branch", e.target.value)} data-testid={`bank-branch-${i}`} />
                <Input placeholder="Account Number" value={b.account_number} onChange={(e) => setBank(i, "account_number", e.target.value)} data-testid={`bank-acc-${i}`} />
                <Input className="col-span-2" placeholder="Account Holder Name" value={b.holder_name} onChange={(e) => setBank(i, "holder_name", e.target.value)} data-testid={`bank-holder-${i}`} />
              </div>
            </div>
          ))}
          <DialogFooter>
            <Button variant="outline" onClick={() => setF(empty)}>Reset</Button>
            <Button onClick={save} data-testid="save-owner-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!view} onOpenChange={() => setView(null)}>
        <DialogContent data-testid="owner-view-dialog">
          <DialogHeader><DialogTitle>{view?.name}</DialogTitle></DialogHeader>
          {view && (
            <div className="space-y-2 text-sm">
              <Row k="Mobile" v={view.phone} /><Row k="Email" v={view.email} /><Row k="PAN" v={view.pan} />
              <Row k="District" v={view.district} /><Row k="State" v={view.state} />
              <div className="pt-2 font-semibold">Bank Accounts</div>
              {(view.banks || []).map((b, i) => (
                <div key={i} className="bg-muted/40 rounded-lg p-3 text-xs space-y-1">
                  <div>{b.bank_name} · {b.branch}</div><div className="font-mono">A/C {b.account_number} · {b.ifsc}</div><div>{b.holder_name}</div>
                </div>
              ))}
              {(view.banks || []).length === 0 && <div className="text-xs text-muted-foreground">No bank accounts</div>}
              <div className="pt-3 font-semibold">Trucks ({vehicles.filter((v) => v.owner_name === view.name).length})</div>
              <div className="space-y-1.5" data-testid="owner-view-trucks">
                {vehicles.filter((v) => v.owner_name === view.name).map((v) => (
                  <div key={v.id} className="flex items-center justify-between bg-muted/40 rounded-lg px-3 py-2 text-xs" data-testid={`owner-view-truck-${v.id}`}>
                    <span className="font-mono font-medium">{v.vehicle_number}</span>
                    <span className="text-muted-foreground">{v.vehicle_type}{v.commission_enabled ? ` · ${v.commission_pct}% comm` : ""}</span>
                  </div>
                ))}
                {vehicles.filter((v) => v.owner_name === view.name).length === 0 && <div className="text-xs text-muted-foreground">No trucks linked to this owner</div>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
const Row = ({ k, v }) => (<div className="flex justify-between border-b border-border py-1.5"><span className="text-muted-foreground">{k}</span><span className="font-medium">{v || "-"}</span></div>);

/* ---------------- Truck Master ---------------- */
function TruckMaster() {
  const { hubs, selectedHub, hubName } = useApp();
  const [rows, setRows] = useState([]);
  const [owners, setOwners] = useState([]);
  const [q, setQ] = useState("");
  const [needsOwner, setNeedsOwner] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [transfer, setTransfer] = useState(null);
  const empty = { vehicle_number: "", vehicle_type: "Open", owner_name: "", owner_phone: "", commission_pct: 0, commission_enabled: false, ownership: "Market", rc_book: "", hub_id: "" };
  const [f, setF] = useState(empty);
  const [tf, setTf] = useState({ to_owner: "", transfer_date: new Date().toISOString().slice(0, 10), reason: "" });

  const load = useCallback(async () => setRows((await api.get("/vehicles", { params: { hub: selectedHub } })).data), [selectedHub]);
  useEffect(() => { load(); api.get("/owners", { params: { hub: selectedHub } }).then((r) => setOwners(r.data)); }, [load, selectedHub]);

  const missingOwner = (r) => r.auto_added && !(r.owner_name || "").trim();
  const missingCount = rows.filter(missingOwner).length;
  const filtered = rows.filter((r) => (!needsOwner || missingOwner(r)) && (!q || (r.vehicle_number + r.owner_name).toLowerCase().includes(q.toLowerCase())));
  const { sorted: osRows, dir: osDir, toggle: osToggle } = useOwnerSort(filtered);
  const { page, setPage, pageSize, setPageSize, totalPages, pageRows, start } = usePaged(osRows);

  const openCreate = () => { setEditing(null); setF({ ...empty, hub_id: selectedHub !== "all" ? selectedHub : hubs[0]?.id || "" }); setOpen(true); };
  const openEdit = (r) => { setEditing(r.id); setF({ ...empty, ...r }); setOpen(true); };
  const onOwner = (name) => { const o = owners.find((x) => x.name === name); setF((p) => ({ ...p, owner_name: name, owner_phone: o?.phone || "" })); };
  const save = async () => {
    if (!f.vehicle_number) return toast.error("Vehicle Number required");
    const payload = { ...f, commission_enabled: !!f.commission_enabled, commission_pct: f.commission_enabled ? (Number(f.commission_pct) || 0) : 0 };
    try {
      if (editing) { await api.put(`/vehicles/${editing}`, payload); toast.success("Truck updated"); }
      else { await api.post("/vehicles", payload); toast.success("Truck added"); }
    } catch (e) { return toast.error(e?.response?.data?.detail || "Could not save truck"); }
    setOpen(false); load();
  };
  const del = async (id) => { await api.delete(`/vehicles/${id}`); toast.success("Deleted"); load(); };
  const doTransfer = async () => {
    if (!tf.to_owner) return toast.error("Select new owner");
    await api.post("/owner-transfers", { transfer_date: tf.transfer_date, vehicle_number: transfer.vehicle_number, from_owner: transfer.owner_name, to_owner: tf.to_owner, reason: tf.reason, hub_id: transfer.hub_id });
    toast.success("Ownership transferred"); setTransfer(null); load();
  };
  const exportCsv = () => {
    const lines = filtered.map((r) => [r.vehicle_number, r.owner_name, r.vehicle_type, r.ownership || "Market", r.commission_pct].join(","));
    const blob = new Blob([["Vehicle Number,Owner Name,Vehicle Type,Ownership,Commission %", ...lines].join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "trucks.csv"; a.click();
  };

  return (
    <div>
      <Toolbar q={q} setQ={setQ} onRefresh={load} onExport={exportCsv} placeholder="Search vehicle / owner"
        right={<><button onClick={() => { setNeedsOwner((v) => !v); setPage(1); }} data-testid="needs-owner-chip"
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${needsOwner ? "bg-rose-500 text-white border-rose-500" : "bg-rose-500/10 text-rose-500 border-rose-500/30 hover:bg-rose-500/20"}`}>
          Needs owner ({missingCount})</button><PageSize value={pageSize} onChange={setPageSize} /><Button onClick={openCreate} data-testid="add-truck-button"><Plus className="h-4 w-4 mr-1" />Add Truck</Button></>} />
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="truck-master-table">
          <thead className="bg-muted/60 text-muted-foreground text-xs uppercase">
            <tr><th className="py-3 px-4 text-left">Sl No</th><th className="py-3 px-4 text-left">Vehicle Number</th><SortHeader label="Owner Name" dir={osDir} onClick={osToggle} /><th className="py-3 px-4 text-left">Vehicle Type</th><th className="py-3 px-4 text-left">Ownership</th><th className="py-3 px-4 text-left">Hub</th><th className="py-3 px-4 text-right">Commission %</th><th className="py-3 px-4 text-center">Action</th></tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => (
              <tr key={r.id} className="border-t border-border hover:bg-accent/40" data-testid={`truck-master-row-${r.id}`}>
                <td className="py-3 px-4 text-muted-foreground">{start + i + 1}</td>
                <td className="py-3 px-4 font-mono font-medium">{r.vehicle_number}</td>
                <td className="py-3 px-4">{missingOwner(r)
                  ? <button onClick={() => openEdit(r)} data-testid={`missing-owner-badge-${r.id}`} title="Auto-added from a challan — click to fill owner"
                      className="text-xs px-2 py-0.5 rounded bg-rose-500/15 text-rose-500 border border-rose-500/30 hover:bg-rose-500/25 transition-colors">Missing owner</button>
                  : r.owner_name}</td>
                <td className="py-3 px-4">{r.vehicle_type}</td>
                <td className="py-3 px-4">{(r.ownership || "Market") === "Own"
                  ? <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-500 border border-emerald-500/20">Own</span>
                  : <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground border border-border">Market</span>}</td>
                <td className="py-3 px-4">{hubName(r.hub_id)}</td>
                <td className="py-3 px-4 text-right font-mono">{r.commission_pct || 0}</td>
                <td className="py-3 px-4">
                  <div className="flex items-center justify-center gap-3">
                    <button onClick={() => { setTransfer(r); setTf({ to_owner: "", transfer_date: new Date().toISOString().slice(0, 10), reason: "" }); }} className="text-cyan-500 hover:opacity-70" title="Transfer owner" data-testid={`transfer-truck-${r.id}`}><ArrowLeftRight className="h-4 w-4" /></button>
                    <button onClick={() => openEdit(r)} className="text-primary hover:opacity-70" data-testid={`edit-truck-${r.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(r.id)} className="text-rose-500 hover:opacity-70" data-testid={`delete-truck-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {pageRows.length === 0 && <tr><td colSpan={8} className="py-16 text-center text-muted-foreground">No trucks</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} totalPages={totalPages} setPage={setPage} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="truck-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Truck" : "Create Truck"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Vehicle Number" req><Input data-testid="truck-number-input" value={f.vehicle_number} onChange={(e) => setF({ ...f, vehicle_number: e.target.value.toUpperCase() })} /></Field>
            <Field label="Vehicle Type" req>
              <Select value={f.vehicle_type} onValueChange={(v) => setF({ ...f, vehicle_type: v })}>
                <SelectTrigger data-testid="truck-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{VEHICLE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Owner">
              <Select value={f.owner_name} onValueChange={onOwner}>
                <SelectTrigger data-testid="truck-owner-select"><SelectValue placeholder="Select owner" /></SelectTrigger>
                <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Hub">
              <Select value={f.hub_id} onValueChange={(v) => setF({ ...f, hub_id: v })}>
                <SelectTrigger data-testid="truck-hub-select"><SelectValue placeholder="Select hub" /></SelectTrigger>
                <SelectContent>{hubs.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="RC Book No"><Input data-testid="truck-rc-input" value={f.rc_book} onChange={(e) => setF({ ...f, rc_book: e.target.value })} /></Field>
            <Field label="Ownership">
              <Select value={f.ownership || "Market"} onValueChange={(v) => setF({ ...f, ownership: v })}>
                <SelectTrigger data-testid="truck-ownership-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Own">Own</SelectItem><SelectItem value="Market">Market / Hired</SelectItem></SelectContent>
              </Select>
            </Field>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Commission</label>
              <div className="flex items-center gap-3 h-9">
                <Switch checked={!!f.commission_enabled} onCheckedChange={(v) => setF({ ...f, commission_enabled: v })} data-testid="truck-commission-toggle" />
                <span className="text-xs text-muted-foreground">{f.commission_enabled ? "Enabled" : "Disabled"}</span>
              </div>
            </div>
            <Field label="Commission %">
              <Input type="number" data-testid="truck-commission-input" disabled={!f.commission_enabled}
                value={f.commission_enabled ? f.commission_pct : ""} placeholder={f.commission_enabled ? "" : "Toggle on to edit"}
                onChange={(e) => setF({ ...f, commission_pct: e.target.value })} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setF(empty)}>Reset</Button>
            <Button onClick={save} data-testid="save-truck-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!transfer} onOpenChange={() => setTransfer(null)}>
        <DialogContent data-testid="transfer-dialog">
          <DialogHeader><DialogTitle>Transfer Ownership</DialogTitle></DialogHeader>
          {transfer && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Vehicle"><Input value={transfer.vehicle_number} disabled /></Field>
              <Field label="Current Owner"><Input value={transfer.owner_name} disabled /></Field>
              <Field label="Transfer Date"><Input type="date" value={tf.transfer_date} onChange={(e) => setTf({ ...tf, transfer_date: e.target.value })} data-testid="transfer-date-input" /></Field>
              <Field label="New Owner" req>
                <Select value={tf.to_owner} onValueChange={(v) => setTf({ ...tf, to_owner: v })}>
                  <SelectTrigger data-testid="transfer-owner-select"><SelectValue placeholder="Select new owner" /></SelectTrigger>
                  <SelectContent>{owners.filter((o) => o.name !== transfer.owner_name).map((o) => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <div className="col-span-2"><Field label="Reason"><Input value={tf.reason} onChange={(e) => setTf({ ...tf, reason: e.target.value })} data-testid="transfer-reason-input" /></Field></div>
            </div>
          )}
          <DialogFooter><Button onClick={doTransfer} data-testid="save-transfer-button"><Check className="h-4 w-4 mr-1" />Confirm Transfer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Master Data ---------------- */
function MasterData() {
  const { selectedHub, hubName } = useApp();
  return (
    <div data-testid="master-data">
      {selectedHub === "all" ? (
        <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-600 dark:text-amber-400" data-testid="master-hub-hint">
          Select a specific hub (top-right) to view and add its material &amp; location lists. Master data is maintained per hub.
        </div>
      ) : (
        <div className="mb-4 text-sm text-muted-foreground">Managing master data for <span className="font-semibold text-foreground">{hubName(selectedHub)}</span></div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <MasterColumn kind="material" title="Material Type" placeholder="Enter Material Type" hub={selectedHub} />
        <MasterColumn kind="load_location" title="Load Location" placeholder="Enter Load Location" hub={selectedHub} />
        <MasterColumn kind="delivery_location" title="Delivery Location" placeholder="Enter Delivery Location" hub={selectedHub} />
      </div>
    </div>
  );
}
function MasterColumn({ kind, title, placeholder, hub }) {
  const [rows, setRows] = useState([]);
  const [val, setVal] = useState("");
  const [q, setQ] = useState("");
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const canEdit = hub && hub !== "all";
  const load = useCallback(async () => setRows((await api.get(`/master/${kind}`, { params: { hub } })).data), [kind, hub]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!canEdit) return toast.error("Select a specific hub to add master data");
    if (!val.trim()) return;
    try { await api.post("/master", { kind, name: val.trim(), hub_id: hub }); setVal(""); toast.success(`${title} added`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };
  const saveEdit = async (id) => {
    try { await api.put(`/master/${id}`, { kind, name: editVal, hub_id: hub }); setEditId(null); toast.success("Updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };
  const del = async (id) => { await api.delete(`/master/${id}`); load(); };
  const filtered = rows.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="bg-card border border-border rounded-xl shadow-sm p-4">
      <div className="flex gap-2 mb-3">
        <Input placeholder={placeholder} value={val} disabled={!canEdit} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid={`master-input-${kind}`} />
        <Button onClick={add} disabled={!canEdit} data-testid={`master-add-${kind}`}>Add</Button>
      </div>
      <Input className="mb-3" placeholder={`Search ${title}`} value={q} onChange={(e) => setQ(e.target.value)} data-testid={`master-search-${kind}`} />
      <div className="max-h-[520px] overflow-y-auto divide-y divide-border" data-testid={`master-list-${kind}`}>
        {filtered.map((r) => (
          <div key={r.id} className="flex items-center justify-between py-2.5 px-1 group">
            {editId === r.id ? (
              <div className="flex gap-2 w-full">
                <Input className="h-8" value={editVal} onChange={(e) => setEditVal(e.target.value)} />
                <Button size="sm" onClick={() => saveEdit(r.id)}>Save</Button>
              </div>
            ) : (
              <>
                <span className="text-sm">{r.name}</span>
                <div className="flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => { setEditId(r.id); setEditVal(r.name); }} className="text-primary" data-testid={`master-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(r.id)} className="text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </>
            )}
          </div>
        ))}
        {filtered.length === 0 && <div className="py-10 text-center text-xs text-muted-foreground">No entries</div>}
      </div>
    </div>
  );
}

/* ---------------- Transfer Log ---------------- */
function TransferLog() {
  const { selectedHub } = useApp();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/owner-transfers", { params: { hub: selectedHub } }).then((r) => setRows(r.data)); }, [selectedHub]);
  const { page, setPage, pageSize, setPageSize, totalPages, pageRows, start } = usePaged(rows);
  return (
    <div>
      <div className="flex justify-end mb-2"><PageSize value={pageSize} onChange={setPageSize} /></div>
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="transfer-log-table">
          <thead className="bg-muted/60 text-muted-foreground text-xs uppercase">
            <tr><th className="py-3 px-4 text-left">Sl No</th><th className="py-3 px-4 text-left">Date</th><th className="py-3 px-4 text-left">Vehicle</th><th className="py-3 px-4 text-left">From Owner</th><th className="py-3 px-4 text-left">To Owner</th><th className="py-3 px-4 text-left">Reason</th></tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => (
              <tr key={r.id} className="border-t border-border hover:bg-accent/40" data-testid={`transfer-log-row-${r.id}`}>
                <td className="py-3 px-4 text-muted-foreground">{start + i + 1}</td>
                <td className="py-3 px-4">{dt(r.transfer_date)}</td>
                <td className="py-3 px-4 font-mono">{r.vehicle_number}</td>
                <td className="py-3 px-4">{r.from_owner}</td>
                <td className="py-3 px-4 font-medium text-cyan-500">{r.to_owner}</td>
                <td className="py-3 px-4 text-muted-foreground">{r.reason || "-"}</td>
              </tr>
            ))}
            {pageRows.length === 0 && <tr><td colSpan={6} className="py-16 text-center text-muted-foreground">No transfers</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} totalPages={totalPages} setPage={setPage} />
    </div>
  );
}

/* ---------------- Activity Log ---------------- */
function ActivityLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/activity-logs").then((r) => setRows(r.data)); }, []);
  const { page, setPage, pageSize, setPageSize, totalPages, pageRows } = usePaged(rows, 25);
  const badge = { Create: "bg-emerald-500/15 text-emerald-500", Transfer: "bg-cyan-500/15 text-cyan-500", Delete: "bg-rose-500/15 text-rose-500" };
  return (
    <div>
      <div className="flex justify-end mb-2"><PageSize value={pageSize} onChange={setPageSize} /></div>
      <div className="bg-card border border-border rounded-xl shadow-sm p-2" data-testid="activity-log">
        {pageRows.map((r) => (
          <div key={r.id} className="flex items-center gap-4 py-3 px-3 border-b border-border last:border-0" data-testid={`activity-row-${r.id}`}>
            <span className={`text-xs font-semibold px-2 py-1 rounded ${badge[r.action] || "bg-muted text-muted-foreground"}`}>{r.action}</span>
            <span className="text-sm font-medium w-24">{r.entity}</span>
            <span className="text-sm text-muted-foreground flex-1">{r.detail}</span>
            <span className="text-xs text-muted-foreground font-mono">{new Date(r.at).toLocaleString("en-GB")}</span>
          </div>
        ))}
        {pageRows.length === 0 && <div className="py-16 text-center text-muted-foreground">No activity</div>}
      </div>
      <Pager page={page} totalPages={totalPages} setPage={setPage} />
    </div>
  );
}
